import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  FormControl, FormLabel, Input, Textarea, Select,
  Button, HStack, Icon
} from "@chakra-ui/react";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaArrowRight } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FINANCE_PRIMARY = "#141C49";
const FINANCE_ACCENT = "#C2A878";
const NAVY = "#141C49";

const contactCards = [
  {
    icon: FaPhone,
    label: "Phone",
    value: "1300 536 159",
    sub: "Monday – Friday, 9am – 5pm AEST"
  },
  {
    icon: FaEnvelope,
    label: "Email",
    value: "hello@onefinanceaustralia.com.au",
    sub: "We typically respond within 24 hours"
  },
  {
    icon: FaMapMarkerAlt,
    label: "Location",
    value: "Brisbane CBD",
    sub: "Servicing clients Australia-wide"
  },
  {
    icon: FaClock,
    label: "Business Hours",
    value: "Mon – Fri: 9:00am – 5:00pm",
    sub: "Saturday by appointment · Sunday closed"
  }
];

const FinanceContact: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/contact"
        fallbackTitle="Contact One Finance Australia"
        fallbackDescription="Get in touch with our finance specialists for a free, no-obligation consultation on home loans, refinancing, investment, and more."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 24 }}
        backgroundImage="url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={5} textAlign="center">
            <Text fontSize="sm" color={FINANCE_ACCENT} textTransform="uppercase" letterSpacing="wider" fontWeight="600">
              One Finance Australia
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Get In Touch
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Free, no-obligation advice from real people who genuinely want to help you get there.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Contact cards + form */}
      <Container maxW="container.xl" py={20}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={14}>

          {/* Left — info */}
          <VStack spacing={6} align="stretch">
            <Box>
              <Heading size="lg" color={NAVY} mb={3}>Our Finance Specialists Are Ready</Heading>
              <Text color="gray.600" fontSize="md" lineHeight="tall">
                Whether you're buying your first home, refinancing, investing, or exploring SMSF options — reach out and we'll point you in the right direction. No jargon, just real help.
              </Text>
            </Box>

            {contactCards.map((card) => (
              <Box
                key={card.label}
                p={6}
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ borderColor: FINANCE_ACCENT, shadow: "md" }}
                transition="all 0.2s"
              >
                <HStack mb={3} spacing={3}>
                  <Icon as={card.icon} color={FINANCE_ACCENT} boxSize={5} />
                  <Heading size="sm" color={NAVY}>{card.label}</Heading>
                </HStack>
                <Text fontWeight="700" fontSize="lg" color={FINANCE_PRIMARY}>{card.value}</Text>
                <Text fontSize="sm" color="gray.500" mt={1}>{card.sub}</Text>
              </Box>
            ))}
          </VStack>

          {/* Right — form */}
          <Box bg="gray.50" p={8} borderRadius="2xl" border="1px solid" borderColor="gray.100">
            <Heading size="lg" color={NAVY} mb={6}>Send Us a Message</Heading>
            <VStack spacing={5} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.700">First Name</FormLabel>
                  <Input bg="white" placeholder="John" size="lg" focusBorderColor={FINANCE_ACCENT} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.700">Last Name</FormLabel>
                  <Input bg="white" placeholder="Smith" size="lg" focusBorderColor={FINANCE_ACCENT} />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel color="gray.700">Email</FormLabel>
                <Input bg="white" type="email" placeholder="john.smith@email.com" size="lg" focusBorderColor={FINANCE_ACCENT} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="gray.700">Phone</FormLabel>
                <Input bg="white" type="tel" placeholder="04XX XXX XXX" size="lg" focusBorderColor={FINANCE_ACCENT} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="gray.700">Inquiry Type</FormLabel>
                <Select bg="white" size="lg" focusBorderColor={FINANCE_ACCENT} placeholder="Select an option">
                  <option>Mortgage / Home Loan</option>
                  <option>Investment Loan</option>
                  <option>Refinancing</option>
                  <option>SMSF Loan</option>
                  <option>Asset Finance</option>
                  <option>Personal Loan</option>
                  <option>General Inquiry</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="gray.700">Message</FormLabel>
                <Textarea
                  bg="white"
                  placeholder="Tell us about your financing needs..."
                  rows={5}
                  size="lg"
                  focusBorderColor={FINANCE_ACCENT}
                />
              </FormControl>

              <Button
                size="lg"
                w="full"
                bg={NAVY}
                color="white"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: "#0f1538" }}
                transition="all 0.2s"
              >
                Send Message
              </Button>

              <Text fontSize="xs" color="gray.500" textAlign="center">
                By submitting this form you agree to our privacy policy and consent to being contacted by our team.
              </Text>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl" textAlign="center">
          <Heading size="xl" mb={4}>Prefer to Talk?</Heading>
          <Text fontSize="lg" mb={8} color="gray.300" maxW="xl" mx="auto">
            Call us directly — no hold music, no call centres. Just a broker who knows what they're talking about.
          </Text>
          <HStack justify="center" spacing={4} flexWrap="wrap">
            <Button
              as="a"
              href="tel:1300536159"
              size="lg"
              bg={FINANCE_ACCENT}
              color="white"
              leftIcon={<FaPhone />}
              _hover={{ bg: "#a8905f" }}
              transition="all 0.2s"
            >
              1300 536 159
            </Button>
            <Button
              as="a"
              href="mailto:hello@onefinanceaustralia.com.au"
              size="lg"
              variant="outline"
              borderColor="white"
              color="white"
              leftIcon={<FaEnvelope />}
              _hover={{ bg: "rgba(255,255,255,0.1)" }}
              transition="all 0.2s"
            >
              hello@onefinanceaustralia.com.au
            </Button>
          </HStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceContact;
